// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/components/ui/status-chip.tsx (`BridgeStatusChip`).
// Wraps:   Signal DS `StatusChip`.
// Overrides: always soft (`tone` accepted and ignored, the solid chip is retired); one size, 20 tall with 8px sides
// (`size` accepted and ignored); a chip labelled "Draft" / "AI draft" (or `draft`) gets `data-chip-look="draft"`, whose
// hollow dashed look is already in `src/styles/showcase-bridge.css` (`span[data-semantic][data-chip-look="draft"]`),
// because the DS chip paints its colours inline. Geometry is applied last so no caller can resize one chip. The DS md
// chip's 44px minimum width is PM's too (PM never resets it), so `data-bridge="status-chip"` lets showcase-billing.css
// restore it against the PA sheet's `min-width: 0`.
import { forwardRef, type ReactNode } from 'react';
import { StatusChip, type StatusChipProps as DsStatusChipProps } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';

export interface StatusChipProps extends DsStatusChipProps {
  draft?: boolean;
}

const isDraftLabel = (children: ReactNode) =>
  typeof children === 'string' && /^(ai\s+)?draft$/i.test(children.trim());

/** The geometry PM's adapter forces on every chip. Exported so the className contract is pinned by a test. */
export const BRIDGE_CHIP_GEOMETRY = 'h-5 shrink-0 self-center px-2 leading-5';

export const BridgeStatusChip = forwardRef<HTMLSpanElement, StatusChipProps>(function BridgeStatusChip(
  { draft, className, children, ...rest },
  ref,
) {
  const isDraft = draft ?? isDraftLabel(children);
  // `tone` and `size` from the caller are spread and then overridden: always soft, the DS default size.
  return (
    <StatusChip
      ref={ref}
      {...rest}
      tone="soft"
      size={undefined}
      data-bridge="status-chip"
      data-chip-look={isDraft ? 'draft' : 'soft'}
      className={cn(className, BRIDGE_CHIP_GEOMETRY)}
    >
      {children}
    </StatusChip>
  );
});
